(() => {
  const ROOT_SELECTOR = '[data-cuticle-canyon]';
  const instances = new WeakMap();
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));

  class CuticleCanyonSoftener {
    constructor(root) {
      this.root = root;
      this.story = root.querySelector('[data-cuticle-canyon-story]');
      this.video = root.querySelector('[data-cuticle-canyon-video]');
      this.chapters = Array.from(root.querySelectorAll('[data-cuticle-canyon-chapter]'));
      this.progress = root.querySelector('[data-cuticle-canyon-progress]');
      this.count = root.querySelector('[data-cuticle-canyon-count]');
      this.statusText = root.querySelector('[data-cuticle-canyon-status-text]');
      this.errorMessage = root.querySelector('[data-cuticle-canyon-error]');
      this.variantSelect = root.querySelector('[data-cc-variant-select]');
      this.productSubmit = root.querySelector('[data-cc-product-submit]');
      this.closingSubmit = root.querySelector('[data-cc-submit]');
      this.price = root.querySelector('[data-cc-price]');
      this.availability = root.querySelector('[data-cc-availability]');
      this.advanceCue = root.querySelector('[data-cuticle-canyon-advance]');
      this.language = root.dataset.language === 'ar' ? 'ar' : 'en';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.motionOverride = new URLSearchParams(window.location.search).get('cc_motion');
      this.mediaOverride = new URLSearchParams(window.location.search).get('cc_media');
      this.saveData = Boolean(navigator.connection && navigator.connection.saveData);
      this.duration = 0;
      this.targetTime = 0;
      this.playhead = 0;
      this.lastFrameTime = 0;
      this.frameId = 0;
      this.seekInFlight = false;
      this.isHydrated = false;
      this.isVisible = false;
      this.isDestroyed = false;
      this.activeIndex = -1;
      this.boundMeasure = this.measure.bind(this);
      this.boundTick = this.tick.bind(this);
      this.boundReducedMotion = this.handleMotionPreference.bind(this);
      this.boundAdvanceStory = this.advanceStory.bind(this);
      this.initialize();
    }

    initialize() {
      if (!this.story || !this.video || this.chapters.length === 0) return;

      document.documentElement.classList.add('cuticle-canyon-softener-document');
      this.bindCommerce();
      this.bindStoryControls();
      this.setChapter(0);
      if (this.mediaOverride === 'error') {
        this.handleError();
        return;
      }
      if (this.motionOverride === 'reduce' || this.reducedMotion.matches || this.saveData) {
        const staticReason = this.saveData ? 'data-saver' : 'reduced-motion';
        this.enableStaticMode(staticReason);
        return;
      }

      this.root.classList.add('is-motion-enabled');
      this.setStatus(this.language === 'ar' ? 'جارٍ تجهيز الرحلة' : 'Preparing the journey');
      this.addListeners();
      this.observe();
      this.measure();
    }

    bindCommerce() {
      if (this.closingSubmit && this.productSubmit) {
        this.closingSubmit.addEventListener('click', () => {
          if (!this.productSubmit.disabled) this.productSubmit.click();
        });
      }

      if (!this.variantSelect || !this.productSubmit) return;
      this.variantSelect.addEventListener('change', () => {
        const option = this.variantSelect.selectedOptions[0];
        if (!option) return;
        const isAvailable = option.dataset.available === 'true';
        const variantId = option.value;
        const priceTemplate = this.root.querySelector(`[data-cc-variant-price="${variantId}"]`);

        this.productSubmit.dataset.variantId = variantId;
        this.productSubmit.disabled = !isAvailable;
        this.productSubmit.setAttribute('aria-disabled', String(!isAvailable));
        if (this.closingSubmit) {
          this.closingSubmit.disabled = !isAvailable;
          this.closingSubmit.setAttribute('aria-disabled', String(!isAvailable));
        }
        if (this.price && priceTemplate) this.price.innerHTML = priceTemplate.innerHTML;

        const availableText = this.language === 'ar' ? 'متوفر الآن' : 'Available now';
        const unavailableText = this.language === 'ar' ? 'غير متوفر حالياً' : 'Currently unavailable';
        const submitText = this.language === 'ar' ? 'أضف منعم اللحية للسلة' : 'Add Beard Softener to cart';
        const label = this.productSubmit.querySelector('[data-cc-submit-label]');
        if (label) label.textContent = isAvailable ? submitText : unavailableText;
        if (this.availability) {
          this.availability.textContent = isAvailable ? `● ${availableText}` : unavailableText;
        }
      });
    }

    bindStoryControls() {
      if (!this.advanceCue) return;
      this.advanceCue.addEventListener('click', this.boundAdvanceStory);
    }

    advanceStory() {
      if (
        this.root.classList.contains('is-static')
        || this.root.classList.contains('is-media-error')
        || this.activeIndex >= this.chapters.length - 1
      ) return;

      const nextIndex = Math.max(0, this.activeIndex + 1);
      const nextChapter = this.chapters[nextIndex];
      const nextStart = Number(nextChapter.dataset.start || nextIndex / this.chapters.length);
      const scrollableDistance = Math.max(1, this.story.offsetHeight - window.innerHeight);
      const storyTop = window.scrollY + this.story.getBoundingClientRect().top;

      window.scrollTo({
        top: storyTop + scrollableDistance * clamp(nextStart + 0.015),
        behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
      });
    }

    addListeners() {
      window.addEventListener('scroll', this.boundMeasure, { passive: true });
      window.addEventListener('resize', this.boundMeasure, { passive: true });
      this.video.addEventListener('loadedmetadata', () => this.handleMetadata());
      this.video.addEventListener('canplay', () => this.markReady());
      this.video.addEventListener('seeked', () => this.handleSeeked());
      this.video.addEventListener('error', () => this.handleError());
      this.video.addEventListener('stalled', () => {
        if (!this.root.classList.contains('is-video-ready')) {
          this.setStatus(this.language === 'ar' ? 'الفيلم يحتاج لحظة إضافية' : 'The film needs another moment');
        }
      });

      if (typeof this.reducedMotion.addEventListener === 'function') {
        this.reducedMotion.addEventListener('change', this.boundReducedMotion);
      } else if (typeof this.reducedMotion.addListener === 'function') {
        this.reducedMotion.addListener(this.boundReducedMotion);
      }
    }

    observe() {
      if (!('IntersectionObserver' in window)) {
        this.isVisible = true;
        this.hydrate();
        return;
      }

      this.observer = new IntersectionObserver((entries) => {
        const entry = entries[0];
        this.isVisible = Boolean(entry && entry.isIntersecting);
        if (entry && (entry.isIntersecting || entry.boundingClientRect.top < window.innerHeight * 1.5)) {
          this.hydrate();
        }
        if (this.isVisible) this.requestTick();
      }, { rootMargin: '140% 0px 140% 0px', threshold: 0 });
      this.observer.observe(this.story);
    }

    hydrate() {
      if (this.isHydrated || this.isDestroyed || this.root.classList.contains('is-static')) return;
      const source = this.video.dataset.src;
      if (!source) {
        this.handleError();
        return;
      }

      this.isHydrated = true;
      this.root.classList.add('is-loading');
      this.video.src = source;
      this.video.load();
    }

    handleMetadata() {
      const actualDuration = Number(this.video.duration);
      if (!Number.isFinite(actualDuration) || actualDuration <= 0) {
        this.handleError();
        return;
      }

      this.duration = actualDuration;
      this.video.pause();
      this.playhead = clamp(this.targetTime, 0, this.maximumTime());
      this.setStatus(this.language === 'ar' ? 'جاهز للسحب' : 'Ready to scroll');
      this.markReady();
      this.measure();
    }

    markReady() {
      if (!this.duration) return;
      this.root.classList.remove('is-loading');
      this.root.classList.add('is-video-ready');
    }

    handleSeeked() {
      this.seekInFlight = false;
      if (this.isDestroyed) return;
      this.requestTick();
    }

    handleError() {
      if (this.isDestroyed) return;
      this.seekInFlight = false;
      this.root.classList.remove('is-loading', 'is-video-ready', 'is-motion-enabled');
      this.root.classList.add('is-media-error');
      if (this.errorMessage) this.errorMessage.hidden = false;
      this.setAllChaptersReadable();
      this.stopTick();
    }

    handleMotionPreference(event) {
      if (event.matches) this.enableStaticMode('reduced-motion');
    }

    enableStaticMode(reason) {
      this.root.classList.remove('is-loading', 'is-video-ready', 'is-motion-enabled');
      this.root.classList.add('is-static');
      this.root.dataset.staticReason = reason;
      this.setAllChaptersReadable();
      this.stopTick();
      if (this.video) {
        this.video.pause();
        this.video.removeAttribute('src');
        this.video.load();
      }
    }

    setAllChaptersReadable() {
      this.chapters.forEach((chapter) => {
        chapter.classList.add('is-active');
        chapter.classList.remove('is-before', 'is-after');
        chapter.removeAttribute('aria-hidden');
        chapter.querySelectorAll('a, button').forEach((control) => control.removeAttribute('tabindex'));
      });
    }

    measure() {
      if (this.isDestroyed || this.root.classList.contains('is-static') || this.root.classList.contains('is-media-error')) return;
      const rect = this.story.getBoundingClientRect();
      const scrollableDistance = Math.max(1, this.story.offsetHeight - window.innerHeight);
      const progress = clamp(-rect.top / scrollableDistance);

      this.root.style.setProperty('--cc-progress', progress.toFixed(5));
      this.targetTime = this.duration ? progress * this.maximumTime() : 0;
      this.updateChapter(progress);
      this.requestTick();
    }

    maximumTime() {
      if (!this.duration) return 0;
      return Math.max(0, this.duration - Math.min(1 / 60, this.duration * 0.002));
    }

    updateChapter(progress) {
      let nextIndex = this.chapters.length - 1;
      for (let index = 0; index < this.chapters.length; index += 1) {
        const chapter = this.chapters[index];
        const start = Number(chapter.dataset.start || 0);
        const end = Number(chapter.dataset.end || 1);
        if (progress >= start && (progress < end || index === this.chapters.length - 1)) {
          nextIndex = index;
          break;
        }
      }
      this.setChapter(nextIndex);
    }

    setChapter(index) {
      if (index === this.activeIndex) return;
      this.activeIndex = index;
      this.chapters.forEach((chapter, chapterIndex) => {
        const isActive = chapterIndex === index;
        chapter.classList.toggle('is-active', isActive);
        chapter.classList.toggle('is-before', chapterIndex < index);
        chapter.classList.toggle('is-after', chapterIndex > index);
        chapter.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        chapter.querySelectorAll('a, button').forEach((control) => {
          if (isActive) control.removeAttribute('tabindex');
          else control.setAttribute('tabindex', '-1');
        });
      });
      if (this.count) this.count.textContent = String(index + 1).padStart(2, '0');
      this.root.dataset.activeChapter = String(index + 1);
    }

    requestTick() {
      if (this.frameId || this.isDestroyed || !this.duration || this.root.classList.contains('is-static') || this.root.classList.contains('is-media-error')) return;
      this.frameId = window.requestAnimationFrame(this.boundTick);
    }

    stopTick() {
      if (this.frameId) window.cancelAnimationFrame(this.frameId);
      this.frameId = 0;
      this.lastFrameTime = 0;
    }

    tick(timestamp) {
      this.frameId = 0;
      if (this.isDestroyed || !this.duration) return;

      const elapsed = this.lastFrameTime ? Math.min(64, timestamp - this.lastFrameTime) : 16.67;
      this.lastFrameTime = timestamp;
      const easing = 1 - Math.exp(-0.012 * elapsed);
      const difference = this.targetTime - this.playhead;

      if (Math.abs(difference) < 0.0015) this.playhead = this.targetTime;
      else this.playhead += difference * easing;

      this.seekLatestPlayhead();

      const needsMoreEasing = Math.abs(this.targetTime - this.playhead) >= 0.0015;
      const seekNeedsCatchup = Math.abs(this.video.currentTime - this.targetTime) > 0.035;
      if (needsMoreEasing || this.seekInFlight || seekNeedsCatchup) this.requestTick();
      else this.lastFrameTime = 0;
    }

    seekLatestPlayhead() {
      if (this.seekInFlight || this.video.readyState < 1) return;
      const nextTime = clamp(this.playhead, 0, this.maximumTime());
      if (Math.abs(this.video.currentTime - nextTime) < 1 / 90) return;

      this.seekInFlight = true;
      try {
        this.video.pause();
        this.video.currentTime = nextTime;
      } catch (error) {
        this.seekInFlight = false;
        if (error && error.name !== 'InvalidStateError') this.handleError();
      }
    }

    setStatus(message) {
      if (this.statusText) this.statusText.textContent = message;
    }

    destroy() {
      this.isDestroyed = true;
      document.documentElement.classList.remove('cuticle-canyon-softener-document');
      this.stopTick();
      window.removeEventListener('scroll', this.boundMeasure);
      window.removeEventListener('resize', this.boundMeasure);
      if (this.advanceCue) {
        this.advanceCue.removeEventListener('click', this.boundAdvanceStory);
      }
      if (this.observer) this.observer.disconnect();
      if (typeof this.reducedMotion.removeEventListener === 'function') {
        this.reducedMotion.removeEventListener('change', this.boundReducedMotion);
      } else if (typeof this.reducedMotion.removeListener === 'function') {
        this.reducedMotion.removeListener(this.boundReducedMotion);
      }
      this.video.pause();
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (instances.has(root)) return;
      const instance = new CuticleCanyonSoftener(root);
      instances.set(root, instance);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector(ROOT_SELECTOR);
    const instance = root ? instances.get(root) : null;
    if (instance) instance.destroy();
  });
})();
